import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiError, getImageUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BRAND_LOGO = getImageUrl("naura-numa/assets/frontend/logo-haniyah-gallery.jpg");
const LOGIN_HERO_IMG = getImageUrl("naura-numa/assets/frontend/login-register-hero-haniyah-20260506.jpg");

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const mode = searchParams.get("mode") || searchParams.get("tab");
    if (location.pathname === "/register" || ["register", "signup", "create-account"].includes((mode || "").toLowerCase())) {
      setIsLogin(false);
    }
  }, [location.pathname, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name, phone);
      }
      toast.success(isLogin ? t("auth.welcome_msg") : t("auth.account_created"));
      navigate(isLogin ? "/" : "/profile");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex" data-testid="login-page">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src={LOGIN_HERO_IMG}
          alt="Fashion"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/30" />
        <div className="absolute bottom-12 left-12">
          <div className="flex items-center gap-4 mb-3">
            <img src={BRAND_LOGO} alt="Haniyah Gallery" className="w-14 h-14 rounded-full object-cover border border-white/30 bg-white" />
            <h2 className="font-serif text-4xl text-white">Haniyah Gallery</h2>
          </div>
          <p className="text-white/70">Premium Modest Fashion</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-8 lg:hidden">
            <img src={BRAND_LOGO} alt="Haniyah Gallery" className="w-11 h-11 rounded-full object-cover border border-stone-200 bg-white" />
            <span className="font-serif text-2xl text-stone-900">Haniyah Gallery</span>
          </Link>
          <h1 className="font-serif text-2xl sm:text-3xl text-stone-900 mb-2" data-testid="auth-title">
            {isLogin ? t("auth.welcome_back") : t("auth.create_account")}
          </h1>
          <p className="text-sm text-stone-500 mb-8">
            {isLogin ? t("auth.sign_in_desc") : t("auth.join_today")}
          </p>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-6" data-testid="auth-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">{t("auth.full_name")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 bg-white" data-testid="register-name-input" />
              </div>
            )}
            {!isLogin && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Nomor WhatsApp</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="mt-1 bg-white"
                  placeholder="Contoh: 081234567890"
                  data-testid="register-phone-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">{t("auth.email")}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 bg-white" data-testid="email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">{t("auth.password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 bg-white" data-testid="password-input" />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground py-3.5 text-sm uppercase tracking-wider hover:opacity-90 transition-colors disabled:opacity-50"
              data-testid="auth-submit-btn"
            >
              {loading ? t("auth.please_wait") : isLogin ? t("auth.sign_in_btn") : t("auth.sign_up_btn")}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center"><span className="bg-[#FAF9F6] px-4 text-xs text-stone-400 uppercase">{t("auth.or")}</span></div>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full border border-stone-200 py-3 text-sm text-stone-700 hover:bg-stone-50 transition-colors flex items-center justify-center gap-2"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t("auth.google_continue")}
          </button>

          <p className="text-center text-sm text-stone-500 mt-6">
            {isLogin ? t("auth.no_account") : t("auth.have_account")}{" "}
            <button onClick={() => { setIsLogin(!isLogin); setError(""); }} className="text-primary hover:opacity-80 font-medium" data-testid="toggle-auth-btn">
              {isLogin ? t("auth.sign_up_link") : t("auth.sign_in_link")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
